import { IResult, UAParser } from 'ua-parser-js';
import { base64IfNeeded } from './base64Utils';
import { requestPlatformInformation } from '@/components/flutter/DeviceInfo';

type NavigatorUABrandVersion = {
  readonly brand: string;
  readonly version: string;
};

type NavigatorUAHighEntropyValues = {
  readonly platform?: string;
  readonly platformVersion?: string;
  readonly model?: string;
  readonly brands?: NavigatorUABrandVersion[];
  readonly fullVersionList?: NavigatorUABrandVersion[];
};

type NavigatorUAData = {
  readonly brands?: NavigatorUABrandVersion[];
  getHighEntropyValues(
    hints: Array<'fullVersionList' | 'model' | 'platformVersion'>
  ): Promise<NavigatorUAHighEntropyValues>;
};

type Navigator = {
  readonly userAgentData?: NavigatorUAData;
};

function getParsedUserAgent(): IResult {
  return UAParser(navigator.userAgent);
}

export async function getRequestConfigurationWithDeviceInfos(isBiometricLogin: boolean | undefined) {
  const appVersionHeader: string[] = [];
  const viewportHeader: string[] = [];
  const platformHeader: string[] = [];
  const deviceModelHeader: string[] = [];
  const browserVersionHeader: string[] = [];
  const authenticationHeader: string[] = [];

  const appVersion = `"iTrack";v="${VERSION}";b="${BUILD_NUMBER}";src=Web`;
  appVersionHeader.push(appVersion);

  const biometricLogin = `"BiometricLogin";s=${localStorage.getItem('biometriclogin')};v=${isBiometricLogin};src=Web`;
  authenticationHeader.push(biometricLogin);

  try {
    const platformInformation = await requestPlatformInformation();

    const appVersion = `"${platformInformation.app.name}";v="${platformInformation.app.version}";p="${platformInformation.app.package}";b="${platformInformation.app.buildNumber}";src=App`;
    appVersionHeader.push(appVersion);

    const appDisplayScreen = `"Screen";w=${platformInformation.display.screen.width};h=${platformInformation.display.screen.height};src=App`;
    viewportHeader.push(appDisplayScreen);
    const appDisplayViewport = `"Viewport";w=${platformInformation.display.viewport.width};h=${platformInformation.display.viewport.height};src=App`;
    viewportHeader.push(appDisplayViewport);
    const appDisplaySafePadding = `"SafePadding";t=${platformInformation.display.safePadding.top};b=${platformInformation.display.safePadding.bottom};l=${platformInformation.display.safePadding.left};r=${platformInformation.display.safePadding.right};src=App`;
    viewportHeader.push(appDisplaySafePadding);

    const appOSVersion = `"${platformInformation.os.name}";v="${platformInformation.os.version}";src=App`;
    platformHeader.push(appOSVersion);
    const appOSOther = `c="${platformInformation.os.codeName}";b="${platformInformation.os.buildId}";sdk="${platformInformation.os.sdk}";src=App`;
    platformHeader.push(appOSOther);

    const appDeviceModel = `"${platformInformation.model.name}";b="${platformInformation.model.brand}";c="${platformInformation.model.codeName}";p="${platformInformation.model.isPhysical}";src=App`;
    deviceModelHeader.push(appDeviceModel);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_error) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userAgentData = (navigator as Navigator).userAgentData;
  if (userAgentData) {
    try {
      const extendedUserAgent = await userAgentData.getHighEntropyValues([
        'fullVersionList',
        'model',
        'platformVersion',
      ]);

      platformHeader.push(`"${extendedUserAgent.platform}";v="${extendedUserAgent.platformVersion ?? ''}";src=UAD`);

      if (extendedUserAgent.model) {
        deviceModelHeader.push(`"${extendedUserAgent.model}";src=UAD`);
      }

      const fakeBrowserNames = ['Not-A.Brand', 'Not;A=Brand'];
      const extendedBrowserVersionList =
        extendedUserAgent.fullVersionList ?? extendedUserAgent.brands ?? userAgentData.brands ?? [];
      browserVersionHeader.push(
        ...extendedBrowserVersionList
          .filter((browserEntry) => !fakeBrowserNames.includes(browserEntry.brand))
          .map((browserEntry) => `"${browserEntry.brand}";v="${browserEntry.version}";src=UAD`)
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {}
  }

  const parsedUserAgent = getParsedUserAgent();

  const uapBrowser = parsedUserAgent.browser;
  if (uapBrowser.name) {
    const uapBrowserVersion = `"${uapBrowser.name}";v="${uapBrowser.version ?? ''}";src=UAP`;
    browserVersionHeader.push(uapBrowserVersion);
  }

  const uapEngine = parsedUserAgent.engine;
  if (uapEngine.name) {
    const uapEngineVersion = `"${uapEngine.name}";v="${uapEngine.version ?? ''}";src=UAP`;
    browserVersionHeader.push(uapEngineVersion);
  }

  const uapDevice = parsedUserAgent.device;
  if (uapDevice.model) {
    const uapDeviceModel = `"${uapDevice.model}";b="${uapDevice.vendor ?? ''}";t="${uapDevice.type ?? ''}";src=UAP`;
    deviceModelHeader.push(uapDeviceModel);
  }

  const uapOS = parsedUserAgent.os;
  if (uapOS.name) {
    const uapOSVersion = `"${uapOS.name}";v="${uapOS.version ?? ''}";src=UAP`;
    platformHeader.push(uapOSVersion);
  }

  const uapCPU = parsedUserAgent.cpu;
  if (uapCPU.architecture) {
    const uapArchitecture = `cpu="${uapCPU.architecture}";src=UAP`;
    platformHeader.push(uapArchitecture);
  }

  viewportHeader.push(`"Screen";w=${window.screen.width};h=${window.screen.height};src=Window`);
  const { innerWidth: width, innerHeight: height } = window;
  if (width > 0 || height > 0) {
    viewportHeader.push(`"Viewport";w=${width};h=${height};src=Window`);
  } else {
    viewportHeader.push(`"Viewport";w=${window.screen.availWidth};h=${window.screen.availHeight};src=Window`);
  }

  return {
    headers: {
      'UDS-App-Version': appVersionHeader.length ? base64IfNeeded(appVersionHeader.join(', ')) : undefined,
      'UDS-Viewport': viewportHeader.length ? base64IfNeeded(viewportHeader.join(', ')) : undefined,
      'UDS-OS-Platform': platformHeader.length ? base64IfNeeded(platformHeader.join(', ')) : undefined,
      'UDS-Device-Model': deviceModelHeader.length ? base64IfNeeded(deviceModelHeader.join(', ')) : undefined,
      'UDS-Browser-Version': browserVersionHeader.length ? base64IfNeeded(browserVersionHeader.join(', ')) : undefined,
      'UDS-Authentication': authenticationHeader.length ? base64IfNeeded(authenticationHeader.join(', ')) : undefined,
    },
  };
}
